import { useEffect, useState } from "react";
import { http } from "../api";
import { HandCoins, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { Modal, PageHeader, AddButton, FormRow, EmptyState } from "../shared";

const donEmpty = { donor_name: "", donor_mobile: "", amount: 0, method: "UPI", reference: "", received_on: "", notes: "" };
const expEmpty = { title: "", amount: 0, category: "Program", method: "Cash", incurred_on: "", notes: "" };
const budEmpty = { title: "", amount: 0, scope: "Campaign", period: "2026" };

function INR(n) { return `₹${Number(n || 0).toLocaleString("en-IN")}`; }

export default function Finance() {
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [donations, setDonations] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, d, e, b] = await Promise.all([
      http.get("/finance/summary"), http.get("/donations"), http.get("/expenses"), http.get("/budgets"),
    ]);
    setSummary(s.data); setDonations(d.data); setExpenses(e.data); setBudgets(b.data);
  };
  useEffect(() => { load(); }, []);

  const openModal = (type) => {
    setModal(type);
    setForm(type === "donation" ? donEmpty : type === "expense" ? expEmpty : budEmpty);
  };

  const save = async () => {
    setSaving(true);
    try {
      const url = modal === "donation" ? "/donations" : modal === "expense" ? "/expenses" : "/budgets";
      await http.post(url, { ...form, amount: Number(form.amount) });
      setModal(null); load();
    } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader eyebrow="MONEY FLOW" title="Finance"
        subtitle="Donations, expenses and budgets — one view of the money"
        actions={<>
          <AddButton onClick={() => openModal("donation")} label="Log donation" testid="donation-add-button" />
          <AddButton onClick={() => openModal("expense")} label="Log expense" testid="expense-add-button" />
          <AddButton onClick={() => openModal("budget")} label="Set budget" testid="budget-add-button" />
        </>} />

      <div className="finance-kpis" data-testid="finance-kpis">
        <div className="stat" data-testid="stat-total-donations">
          <div className="stat-icon green"><TrendingUp size={19} /></div>
          <span>Total donations</span><strong>{summary ? INR(summary.total_donations) : "—"}</strong>
          <small className="trend">{summary?.donations_count || 0} entries</small>
        </div>
        <div className="stat" data-testid="stat-total-expenses">
          <div className="stat-icon amber"><TrendingDown size={19} /></div>
          <span>Total expenses</span><strong>{summary ? INR(summary.total_expenses) : "—"}</strong>
          <small className="trend">{summary?.expenses_count || 0} entries</small>
        </div>
        <div className="stat" data-testid="stat-net-balance">
          <div className="stat-icon teal"><Wallet size={19} /></div>
          <span>Net balance</span><strong>{summary ? INR(summary.net) : "—"}</strong>
          <small className="trend">{summary && summary.net >= 0 ? "Surplus" : "Deficit"}</small>
        </div>
        <div className="stat" data-testid="stat-budgets">
          <div className="stat-icon blue"><HandCoins size={19} /></div>
          <span>Active budgets</span><strong>{budgets.length}</strong>
          <small className="trend">{INR(budgets.reduce((s, b) => s + Number(b.amount || 0), 0))}</small>
        </div>
      </div>

      <div className="chip-row">
        {["summary", "donations", "expenses", "budgets"].map((t) => (
          <button key={t} className={`chip-filter ${tab === t ? "active" : ""}`} data-testid={`finance-tab-${t}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === "summary" && summary && (
        <div className="dashboard-grid">
          <section className="panel">
            <div className="panel-head"><h2>By method (donations)</h2><p>Cash / Bank / UPI split</p></div>
            <div className="method-list">
              {Object.entries(summary.by_method).length === 0 && <p className="hint" style={{ padding: 18 }}>No donations yet.</p>}
              {Object.entries(summary.by_method).map(([k, v]) => (
                <div key={k} className="method-row" data-testid={`method-row-${k}`}>
                  <b>{k}</b>
                  <div className="progress-bar" style={{ flex: 1, margin: "0 12px" }}>
                    <div className="progress-fill" style={{ width: `${Math.min(100, (v / summary.total_donations) * 100)}%` }} />
                  </div>
                  <span>{INR(v)}</span>
                </div>
              ))}
            </div>
          </section>
          <section className="panel">
            <div className="panel-head"><h2>By category (expenses)</h2><p>Where the money goes</p></div>
            <div className="method-list">
              {Object.entries(summary.by_category).length === 0 && <p className="hint" style={{ padding: 18 }}>No expenses yet.</p>}
              {Object.entries(summary.by_category).map(([k, v]) => (
                <div key={k} className="method-row" data-testid={`category-row-${k}`}>
                  <b>{k}</b>
                  <div className="progress-bar" style={{ flex: 1, margin: "0 12px" }}>
                    <div className="progress-fill" style={{ width: `${Math.min(100, (v / summary.total_expenses) * 100)}%`, background: "linear-gradient(90deg, #c78118, #efb64e)" }} />
                  </div>
                  <span>{INR(v)}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === "donations" && (
        <section className="panel">
          {donations.length === 0 ? <EmptyState icon={HandCoins} title="No donations" hint="Log a donation to begin." /> : (
            <div className="finance-table">
              <div className="tr th"><span>Donor</span><span>Amount</span><span>Method</span><span>Received</span><span>Reference</span></div>
              {donations.map((d, i) => (
                <div key={d.donation_id} className="tr" data-testid={`donation-row-${i}`}>
                  <span><b>{d.donor_name}</b><small> {d.donor_mobile}</small></span>
                  <span><b>{INR(d.amount)}</b></span>
                  <span><span className="chip blue">{d.method}</span></span>
                  <span>{d.received_on || "—"}</span>
                  <span>{d.reference || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "expenses" && (
        <section className="panel">
          {expenses.length === 0 ? <EmptyState icon={TrendingDown} title="No expenses" hint="Log an expense to begin." /> : (
            <div className="finance-table">
              <div className="tr th"><span>Title</span><span>Amount</span><span>Category</span><span>Method</span><span>Incurred</span></div>
              {expenses.map((e, i) => (
                <div key={e.expense_id} className="tr" data-testid={`expense-row-${i}`}>
                  <span><b>{e.title}</b><small> {e.notes}</small></span>
                  <span><b>{INR(e.amount)}</b></span>
                  <span><span className="chip amber">{e.category}</span></span>
                  <span>{e.method}</span>
                  <span>{e.incurred_on || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "budgets" && (
        <section className="panel">
          {budgets.length === 0 ? <EmptyState icon={Wallet} title="No budgets set" hint="Set a budget to track spending." /> : (
            <div className="finance-table">
              <div className="tr th"><span>Title</span><span>Amount</span><span>Scope</span><span>Period</span><span></span></div>
              {budgets.map((b, i) => (
                <div key={b.budget_id} className="tr" data-testid={`budget-row-${i}`}>
                  <span><b>{b.title}</b></span>
                  <span><b>{INR(b.amount)}</b></span>
                  <span>{b.scope}</span>
                  <span>{b.period}</span>
                  <span></span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {modal && (
        <Modal title={modal === "donation" ? "Log donation" : modal === "expense" ? "Log expense" : "Set budget"} onClose={() => setModal(null)} testid={`${modal}-modal`}>
          {modal === "donation" && (
            <>
              <FormRow label="Donor name"><input data-testid="form-donor-name" value={form.donor_name || ""} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} /></FormRow>
              <div className="form-grid">
                <FormRow label="Donor mobile"><input data-testid="form-donor-mobile" value={form.donor_mobile || ""} onChange={(e) => setForm({ ...form, donor_mobile: e.target.value })} /></FormRow>
                <FormRow label="Amount (₹)"><input data-testid="form-amount" type="number" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></FormRow>
                <FormRow label="Method"><select data-testid="form-method" value={form.method || "UPI"} onChange={(e) => setForm({ ...form, method: e.target.value })}><option>Cash</option><option>Bank</option><option>UPI</option><option>Cheque</option></select></FormRow>
                <FormRow label="Received on"><input data-testid="form-received" type="date" value={form.received_on || ""} onChange={(e) => setForm({ ...form, received_on: e.target.value })} /></FormRow>
              </div>
              <FormRow label="Reference / txn no"><input data-testid="form-reference" value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></FormRow>
            </>
          )}
          {modal === "expense" && (
            <>
              <FormRow label="Title"><input data-testid="form-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormRow>
              <div className="form-grid">
                <FormRow label="Amount (₹)"><input data-testid="form-amount" type="number" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></FormRow>
                <FormRow label="Category"><select data-testid="form-category" value={form.category || "Program"} onChange={(e) => setForm({ ...form, category: e.target.value })}><option>Program</option><option>Complaint</option><option>Office</option><option>Travel</option><option>General</option></select></FormRow>
                <FormRow label="Method"><select data-testid="form-method" value={form.method || "Cash"} onChange={(e) => setForm({ ...form, method: e.target.value })}><option>Cash</option><option>Bank</option><option>UPI</option></select></FormRow>
                <FormRow label="Incurred on"><input data-testid="form-incurred" type="date" value={form.incurred_on || ""} onChange={(e) => setForm({ ...form, incurred_on: e.target.value })} /></FormRow>
              </div>
              <FormRow label="Notes"><input data-testid="form-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormRow>
            </>
          )}
          {modal === "budget" && (
            <>
              <FormRow label="Budget title"><input data-testid="form-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormRow>
              <div className="form-grid">
                <FormRow label="Amount (₹)"><input data-testid="form-amount" type="number" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></FormRow>
                <FormRow label="Scope"><select data-testid="form-scope" value={form.scope || "Campaign"} onChange={(e) => setForm({ ...form, scope: e.target.value })}><option>Campaign</option><option>Ward</option><option>General</option></select></FormRow>
                <FormRow label="Period"><input data-testid="form-period" value={form.period || "2026"} onChange={(e) => setForm({ ...form, period: e.target.value })} /></FormRow>
              </div>
            </>
          )}
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setModal(null)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !Number(form.amount) || (modal === "donation" ? !form.donor_name : !form.title)} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </Modal>
      )}
    </>
  );
}
